export interface ImageSize {
  width: number;
  height: number;
}

export interface CropBox extends ImageSize {
  x: number;
  y: number;
}

export interface AspectRatioBounds {
  min: number;
  max: number;
}

export const COMPANY_LOGO_MAX_WIDTH = 320;
export const COMPANY_LOGO_MAX_HEIGHT = 132;
export const COMPANY_LOGO_MIN_ASPECT_RATIO = 3 / 4;
export const COMPANY_LOGO_ASPECT_RATIO_BOUNDS: AspectRatioBounds = {
  min: COMPANY_LOGO_MIN_ASPECT_RATIO,
  max: COMPANY_LOGO_MAX_WIDTH / COMPANY_LOGO_MAX_HEIGHT,
};
export const COMPANY_LOGO_MAX_SIZE: ImageSize = { width: COMPANY_LOGO_MAX_WIDTH, height: COMPANY_LOGO_MAX_HEIGHT };

export const COMPANY_LOGO_HEADER_HEIGHT = 120;
export const COMPANY_LOGO_HEADER_MAX_WIDTH = Math.round(
  COMPANY_LOGO_HEADER_HEIGHT * COMPANY_LOGO_ASPECT_RATIO_BOUNDS.max
);

const COMPANY_LOGO_HEADER_STYLE = [
  `height: ${COMPANY_LOGO_HEADER_HEIGHT}px`,
  `max-width: ${COMPANY_LOGO_HEADER_MAX_WIDTH}px`,
  'object-fit: contain',
].join('; ');
const COMPANY_LOGO_EMAIL_MARGIN_TOP = 16;
const COMPANY_LOGO_EMAIL_STYLE = [
  'display: block',
  `margin-top: ${COMPANY_LOGO_EMAIL_MARGIN_TOP}px`,
  `max-height: ${COMPANY_LOGO_MAX_HEIGHT}px`,
  `max-width: ${COMPANY_LOGO_MAX_WIDTH}px`,
].join('; ');

export const buildCompanyLogoHeaderImgTag = (logoUrl: string): string =>
  `<img src="${logoUrl}" style="${COMPANY_LOGO_HEADER_STYLE}" />`;

export const buildCompanyLogoEmailImgTag = (logoSource: string): string =>
  `<img src="${logoSource}" style="${COMPANY_LOGO_EMAIL_STYLE}" />`;

export const clampCropToAspectRatioBounds = (
  crop: CropBox,
  bounds: AspectRatioBounds,
  imageSize: ImageSize
): CropBox => {
  const { x, y, width, height } = crop;
  if (width <= 0 || height <= 0) {
    return crop;
  }
  const ratio = width / height;
  const boundedRatio = Math.min(Math.max(ratio, bounds.min), bounds.max);
  if (boundedRatio === ratio) {
    return crop;
  }
  const availableHeight = imageSize.height - y;
  const clampedHeight = Math.min(width / boundedRatio, availableHeight);
  return { x, y, width: clampedHeight * boundedRatio, height: clampedHeight };
};

export const getCenteredCropWithinBounds = (imageSize: ImageSize, bounds: AspectRatioBounds): CropBox => {
  const fullImageCrop = clampCropToAspectRatioBounds({ x: 0, y: 0, ...imageSize }, bounds, imageSize);
  return {
    ...fullImageCrop,
    x: (imageSize.width - fullImageCrop.width) / 2,
    y: (imageSize.height - fullImageCrop.height) / 2,
  };
};

export const fitSizeInBox = (size: ImageSize, box: ImageSize): ImageSize => {
  const scale = Math.min(box.width / size.width, box.height / size.height);
  return { width: Math.round(size.width * scale), height: Math.round(size.height * scale) };
};

export const IMG_LOGO_CID_NUMBER = '123456789';
export const IMG_LOGO_CID_TAG = buildCompanyLogoEmailImgTag(`cid:${IMG_LOGO_CID_NUMBER}`);
